"use client";

import { motion, useReducedMotion, type Variants } from "motion/react";
import { Children, isValidElement, type ReactNode } from "react";
import { cn } from "@/lib/utils";

interface CascadeInProps {
  keySignature: string;
  children: ReactNode;
  className?: string;
  itemClassName?: string;
  staggerMs?: number;
  delayMs?: number;
  duration?: number;
  blur?: number;
  y?: number;
}

export function CascadeIn({
  keySignature,
  children,
  className,
  itemClassName,
  staggerMs = 22,
  delayMs = 40,
  duration = 0.32,
  blur = 6,
  y = 6,
}: CascadeInProps) {
  const reduceMotion = useReducedMotion() ?? false;
  const shiftY = reduceMotion ? 0 : y;
  const blurPx = reduceMotion ? 0 : blur;

  const container: Variants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: reduceMotion ? 0 : staggerMs / 1000,
        delayChildren: reduceMotion ? 0 : delayMs / 1000,
      },
    },
  };

  const item: Variants = {
    hidden: { opacity: 0, y: shiftY, filter: `blur(${blurPx}px)` },
    show: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: { duration, ease: [0.4, 0, 0.2, 1] },
    },
  };

  return (
    <motion.div
      key={keySignature}
      className={cn(className)}
      variants={container}
      initial="hidden"
      animate="show"
    >
      {Children.map(children, (child, i) => (
        <motion.div
          key={isValidElement(child) && child.key != null ? child.key : i}
          variants={item}
          className={itemClassName}
        >
          {child}
        </motion.div>
      ))}
    </motion.div>
  );
}
